<!--
  UserElement.vue

  Internal component that displays a single user chip.
  Used as a building block by UsersElement.

  Props:
    - value: User object (required) - Must have name and id
    - readonly: Hide delete button when true (default: false)
    - size: Chip size ('small', 'normal', 'medium', 'large')
    - fixed: Disable navigation link (default: false) - used for dropdown
      previews, where a click should select the suggestion, not navigate

  Emits:
    - remove: Emitted when delete button is clicked with the user
-->
<template>
  <div class="tags user-tag m-0" :class="{ 'has-addons': !readonly }">
    <AugerLink
      v-if="value.record && !fixed"
      class="is-elided"
      :class="tagClass"
      :target="recordTarget(value.record)"
    >
      <span class="icon mr-2">
        <font-awesome-icon icon="user-circle" />
      </span>
      <span class="font-semibold is-elided">{{ displayName }}</span>
    </AugerLink>
    <span v-else :class="tagClass" class="is-elided">
      <span class="icon mr-2">
        <font-awesome-icon icon="user-circle" />
      </span>
      <span class="font-semibold is-elided">{{ displayName }}</span>
    </span>
    <a v-if="!readonly" class="is-delete" :class="tagClass" @click="handleDelete"></a>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import AugerLink from "../link/AugerLink.vue"
import { recordTarget } from "../link/registry"

interface Props {
  value: Record<string, any>
  readonly?: boolean
  size?: string
  fixed?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  readonly: false,
  size: "normal",
  fixed: false,
})

const emit = defineEmits<{
  remove: [user: Record<string, any>]
}>()

const displayName = computed(() => {
  const name = props.value.name || ""
  if (props.size === "small") return name.split(" ")[0] || name
  return name
})

const tagClass = computed(() => ({
  tag: true,
  [`is-${props.size}`]: true,
  "is-link": props.value.id,
  "bg-dark": !props.value.id,
  "is-rounded": true,
  "text-inverted": true,
}))

function handleDelete() {
  emit("remove", props.value)
}
</script>

<style>
.tags.user-tag {
  flex-wrap: nowrap;
  max-width: 100%;
  min-width: 0;

  .tag {
    &.is-delete {
      flex-shrink: 0;
    }
  }
}
</style>
